import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getReasonLikes, likeReason, unlikeReason } from '../../../firebase/solutionReasonLikesApi.js';

// Async thunks
export const fetchReasonLikes = createAsyncThunk(
  'solutionView/reasonLikes/fetch',
  async ({ reasonId, userId }) => {
    return await getReasonLikes(reasonId, userId);
  }
);

export const addReasonLike = createAsyncThunk(
  'solutionView/reasonLikes/like',
  async ({ reasonId, userId }) => {
    return await likeReason(reasonId, userId);
  }
);

export const removeReasonLike = createAsyncThunk(
  'solutionView/reasonLikes/unlike',
  async ({ reasonId, userId }) => {
    return await unlikeReason(reasonId, userId);
  }
);

const reasonLikesSlice = createSlice({
  name: 'solutionViewReasonLikes',
  initialState: {
    likesByReasonId: {} // { [reasonId]: { likes: number, userLiked: boolean, loading: false, error: null } }
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchReasonLikes.pending, (state, action) => {
        const { reasonId } = action.meta.arg;
        state.likesByReasonId[reasonId] = state.likesByReasonId[reasonId] || {};
        state.likesByReasonId[reasonId].loading = true;
        state.likesByReasonId[reasonId].error = null;
      })
      .addCase(fetchReasonLikes.fulfilled, (state, action) => {
        const { reasonId, likes, userLiked } = action.payload;
        state.likesByReasonId[reasonId] = {
          likes,
          userLiked,
          loading: false,
          error: null
        };
      })
      .addCase(fetchReasonLikes.rejected, (state, action) => {
        const { reasonId } = action.meta.arg;
        state.likesByReasonId[reasonId] = state.likesByReasonId[reasonId] || {};
        state.likesByReasonId[reasonId].loading = false;
        state.likesByReasonId[reasonId].error = action.error.message;
      })
      // Optimistic like
      .addCase(addReasonLike.pending, (state, action) => {
        const { reasonId } = action.meta.arg;
        state.likesByReasonId[reasonId] = state.likesByReasonId[reasonId] || { likes: 0, userLiked: false };
        const entry = state.likesByReasonId[reasonId];
        if (!entry.userLiked) {
          entry.userLiked = true;
          entry.likes = (entry.likes || 0) + 1;
        }
        entry.loading = true;
        entry.error = null;
      })
      .addCase(addReasonLike.fulfilled, (state, action) => {
        const { reasonId, likes, userLiked } = action.payload;
        state.likesByReasonId[reasonId] = {
          likes,
          userLiked,
          loading: false,
          error: null
        };
      })
      .addCase(addReasonLike.rejected, (state, action) => {
        const { reasonId } = action.meta.arg;
        state.likesByReasonId[reasonId] = state.likesByReasonId[reasonId] || { likes: 0, userLiked: false };
        const entry = state.likesByReasonId[reasonId];
        // Roll back optimistic like
        if (entry.userLiked) {
          entry.userLiked = false;
          entry.likes = Math.max(0, (entry.likes || 1) - 1);
        }
        entry.loading = false;
        entry.error = action.error.message;
      })
      // Optimistic unlike
      .addCase(removeReasonLike.pending, (state, action) => {
        const { reasonId } = action.meta.arg;
        state.likesByReasonId[reasonId] = state.likesByReasonId[reasonId] || { likes: 0, userLiked: false };
        const entry = state.likesByReasonId[reasonId];
        if (entry.userLiked) {
          entry.userLiked = false;
          entry.likes = Math.max(0, (entry.likes || 1) - 1);
        }
        entry.loading = true;
        entry.error = null;
      })
      .addCase(removeReasonLike.fulfilled, (state, action) => {
        const { reasonId, likes, userLiked } = action.payload;
        state.likesByReasonId[reasonId] = {
          likes,
          userLiked,
          loading: false,
          error: null
        };
      })
      .addCase(removeReasonLike.rejected, (state, action) => {
        const { reasonId } = action.meta.arg;
        state.likesByReasonId[reasonId] = state.likesByReasonId[reasonId] || { likes: 0, userLiked: false };
        const entry = state.likesByReasonId[reasonId];
        // Roll back optimistic unlike by re-liking
        if (!entry.userLiked) {
          entry.userLiked = true;
          entry.likes = (entry.likes || 0) + 1;
        }
        entry.loading = false;
        entry.error = action.error.message;
      });
  }
});

export default reasonLikesSlice.reducer;
